/**
 * A receipt as ESC/POS: the command language nearly every receipt printer
 * speaks, whoever made it. Sent by the till app (native.ts) to a printer on
 * the network, on USB or on Bluetooth.
 *
 * WORDS IN ANY LANGUAGE
 *
 * A receipt printer prints text from a code page of its own: 256 characters,
 * chosen by a command. This one picks PC858 (Western European, with the euro),
 * which nearly every printer has under the same number. A line it cannot
 * spell (Arabic, Greek, Cyrillic, Chinese, Thai…) is printed as a picture of
 * the line instead, drawn by the tablet, which every printer can print. So a
 * receipt in any language comes out right, and a Latin one stays fast. A
 * printer whose code page 19 is not PC858 can be set to print every line as a
 * picture ("image" mode).
 *
 * Pure: the drawing of a line is passed in (`rasterize`), so the bytes are
 * tested here without a screen, and drawn on the tablet by escpos-raster.ts.
 */

import type { PrintLine } from '@restaurant/ui';

/** One line drawn as dots: rows of `widthBytes` bytes, the left dot in the high bit, 1 is black. */
export interface Raster {
  widthBytes: number;
  height: number;
  data: Uint8Array;
}

export type Rasterize = (line: PrintLine, dots: number) => Raster;

/** How the printer is sent words: as text where it can spell them, or always as pictures. */
export type TextMode = 'text' | 'image';

export interface EscposJob {
  lines: PrintLine[];
  cut: boolean;
  drawer: boolean;
}

const ESC = 0x1b;
const GS = 0x1d;
const LF = 0x0a;

/** PC858's upper half, 0x80 to 0xFF. */
const PC858 =
  'ÇüéâäàåçêëèïîìÄÅ' +
  'ÉæÆôöòûùÿÖÜø£Ø×ƒ' +
  'áíóúñÑªº¿®¬½¼¡«»' +
  '░▒▓│┤ÁÂÀ©╣║╗╝¢¥┐' +
  '└┴┬├─┼ãÃ╚╔╩╦╠═╬¤' +
  'ðÐÊËÈ€ÍÎÏ┘┌█▄¦Ì▀' +
  'ÓßÔÒõÕµþÞÚÛÙýÝ¯´' +
  '\u00ad±‗¾¶§÷¸°¨·¹³²■\u00a0';

/** Typography a receipt does not need, spelled in what the printer has. */
const PLAIN: Record<string, string> = {
  '‘': "'",
  '’': "'",
  '‚': ',',
  '“': '"',
  '”': '"',
  '„': '"',
  '–': '-',
  '—': '-',
  '−': '-',
  '…': '...',
  '\u202f': ' ',
  '\u2009': ' ',
  '\t': ' ',
};

/** The line in PC858, or null when it has a character PC858 does not. */
export function encodePc858(text: string): Uint8Array | null {
  const out: number[] = [];
  for (const raw of text) {
    for (const ch of PLAIN[raw] ?? raw) {
      const code = ch.codePointAt(0)!;
      if (code >= 0x20 && code < 0x7f) {
        out.push(code);
        continue;
      }
      const at = PC858.indexOf(ch);
      if (at < 0) return null;
      out.push(0x80 + at);
    }
  }
  return Uint8Array.from(out);
}

/** Dots across the paper at 203 dpi: 576 on 80mm, 384 on 58mm. */
export function paperDots(width: 48 | 32): number {
  return width === 32 ? 384 : 576;
}

/** The receipt, as the bytes to send the printer. */
export function escposBytes(
  job: EscposJob,
  options: { width: 48 | 32; mode: TextMode; rasterize: Rasterize },
): Uint8Array {
  const out: number[] = [];
  const push = (...bytes: number[]) => out.push(...bytes);

  push(ESC, 0x40); // initialise: forget whatever the last job left set
  push(ESC, 0x74, 19); // code page PC858

  for (const line of job.lines) {
    const text = options.mode === 'text' ? encodePc858(line.text) : null;
    if (text) {
      push(ESC, 0x61, line.align === 'center' ? 1 : 0);
      push(ESC, 0x45, line.bold ? 1 : 0);
      push(GS, 0x21, line.big ? 0x11 : 0x00);
      push(...text, LF);
      continue;
    }
    // The picture carries its own alignment and weight; the printer's own
    // settings would only move it.
    push(ESC, 0x61, 0, ESC, 0x45, 0, GS, 0x21, 0);
    const raster = options.rasterize(line, paperDots(options.width));
    push(GS, 0x76, 0x30, 0); // print raster image, normal size
    push(
      raster.widthBytes & 0xff,
      raster.widthBytes >> 8,
      raster.height & 0xff,
      raster.height >> 8,
    );
    for (const byte of raster.data) out.push(byte);
  }

  if (job.lines.length > 0 && job.cut) {
    push(ESC, 0x61, 0);
    push(GS, 0x56, 0x42, 3); // feed past the cutter, then cut
  }
  if (job.drawer) push(ESC, 0x70, 0, 25, 250); // pulse drawer pin 2
  return Uint8Array.from(out);
}

/** `192.168.1.40` or `192.168.1.40:9100`, as the place to send bytes. */
export function networkPrinter(address: string): { host: string; port: number } | null {
  const trimmed = address
    .trim()
    .replace(/^[a-z]+:\/\//i, '')
    .replace(/\/.*$/, '');
  if (!trimmed) return null;
  const bracketed = /^\[([^\]]+)\](?::(\d+))?$/.exec(trimmed);
  if (bracketed) return { host: bracketed[1]!, port: Number(bracketed[2] ?? 9100) };
  const parts = trimmed.split(':');
  if (parts.length === 2 && /^\d+$/.test(parts[1]!)) {
    return { host: parts[0]!, port: Number(parts[1]) };
  }
  return { host: trimmed, port: 9100 };
}
